/**
 * React Query hooks for Session Invites & Participants (Group Play)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { sessionsApi } from '@api/endpoints/sessions'
import { sessionKeys } from './useSession'
import type { CreateSessionInviteRequest } from '@api/types/session'

// ===================
// Query Hooks
// ===================

/**
 * Get invite list for a session
 */
export function useSessionInvites(sessionId: string | null, enabled = true) {
  return useQuery({
    queryKey: sessionKeys.invites(sessionId || ''),
    queryFn: () => sessionsApi.getInvites(sessionId!),
    enabled: !!sessionId && enabled,
  })
}

/**
 * Get participants for a session (with polling)
 */
export function useSessionParticipants(sessionId: string | null, enabled = true) {
  return useQuery({
    queryKey: sessionKeys.participants(sessionId || ''),
    queryFn: () => sessionsApi.getParticipants(sessionId!),
    enabled: !!sessionId && enabled,
    refetchInterval: 10000, // Poll every 10s
  })
}

// ===================
// Mutation Hooks
// ===================

/**
 * Create an invite for a session
 */
export function useCreateInvite(sessionId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateSessionInviteRequest) => {
      if (!sessionId) throw new Error('Session ID is required')
      return sessionsApi.createInvite(sessionId, data)
    },
    onSuccess: () => {
      if (sessionId) {
        queryClient.invalidateQueries({
          queryKey: sessionKeys.invites(sessionId),
        })
      }
    },
  })
}

/**
 * Accept an invite
 */
export function useAcceptInvite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ sessionId, inviteId }: { sessionId: string; inviteId: string }) =>
      sessionsApi.acceptInvite(sessionId, inviteId),
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: sessionKeys.invites(sessionId),
      })
    },
  })
}

/**
 * Decline an invite
 */
export function useDeclineInvite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ sessionId, inviteId }: { sessionId: string; inviteId: string }) =>
      sessionsApi.declineInvite(sessionId, inviteId),
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: sessionKeys.invites(sessionId),
      })
    },
  })
}

/**
 * Generate a share code for a session
 */
export function useGenerateShareCode(sessionId: string | null) {
  return useMutation({
    mutationFn: () => {
      if (!sessionId) throw new Error('Session ID is required')
      return sessionsApi.generateShareCode(sessionId)
    },
  })
}

/**
 * Toggle anonymity for a session
 */
export function useToggleAnonymity(sessionId: string | null) {
  return useMutation({
    mutationFn: (isAnonymous: boolean) => {
      if (!sessionId) throw new Error('Session ID is required')
      return sessionsApi.toggleAnonymity(sessionId, isAnonymous)
    },
  })
}
